import type {
  BatchResponse,
  DemoLink,
  DemoLinkAccessPolicy,
  DemoLinkAssetType,
  DemoLinkMetadata,
  DemoLinkPlatform,
} from '@carboncopyco/types';
import { handleBatchRequest } from '../utils';
import { mainApi } from './mainApi';

type GetDemoLinksArgs = {
  organizationId: string;
  assetType: DemoLinkAssetType;
  assetId: string;
};

export type AssetUpdateRequest = {
  revisionId: number;
  autoSync: boolean;
};

interface CreateDemoLinkBody {
  enabled: boolean;
  platform: DemoLinkPlatform;
  withGiveADemo: boolean;
  asset: AssetUpdateRequest;
  allowSignUp: boolean;
  accessPolicy: DemoLinkAccessPolicy;
  showGuidanceTooltip?: boolean;
}

interface CreateDemoLinkArgs extends CreateDemoLinkBody {
  organizationId: string;
  assetType: DemoLinkAssetType;
  assetId: string;
}

interface UpdateDemoLinkArgs extends CreateDemoLinkBody {
  organizationId: string;
  id: string;
}

type GetDemoLinkMetadataArgs = {
  token: string;
};

type AcceptDemoLinkArgs = {
  token: string;
};

export const demoLinkApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getDemoLinks: builder.query<BatchResponse<DemoLink>, GetDemoLinksArgs>({
      query: ({ organizationId, assetType, assetId }) =>
        handleBatchRequest({
          url: `/api/${organizationId}/demo-link/${assetType}/${assetId}`,
        }),
      providesTags: ['DemoLinks'],
    }),
    createDemoLink: builder.mutation<DemoLink, CreateDemoLinkArgs>({
      query: ({ organizationId, assetType, assetId, ...rest }) => ({
        url: `/api/${organizationId}/demo-link/${assetType}/${assetId}`,
        method: 'POST',
        body: { ...rest },
      }),
      invalidatesTags: ['DemoLinks'],
    }),
    updateDemoLink: builder.mutation<DemoLink, UpdateDemoLinkArgs>({
      query: ({ organizationId, id, ...rest }) => ({
        url: `/api/${organizationId}/demo-link/${id}`,
        method: 'PUT',
        body: { ...rest },
      }),
      invalidatesTags: ['DemoLinks'],
    }),
    getDemoLinkMetadata: builder.query<
      DemoLinkMetadata,
      GetDemoLinkMetadataArgs
    >({
      query: ({ token }) => ({
        url: `/demo-link/${token}`,
      }),
    }),
    acceptDemoLink: builder.mutation<DemoLink, AcceptDemoLinkArgs>({
      query: ({ token }) => ({
        url: '/api/demo-link/accept',
        method: 'PUT',
        body: { token },
      }),
    }),
  }),
});

export const {
  useGetDemoLinksQuery,
  useLazyGetDemoLinksQuery,
  useCreateDemoLinkMutation,
  useUpdateDemoLinkMutation,
  useGetDemoLinkMetadataQuery,
  useAcceptDemoLinkMutation,
} = demoLinkApi;
